<script>
    import {
        vulnerabilities,
        hydrateVulnerabilitiesWithTags,
        replaceVulnerabilitiesListElements,
    } from "../vulnerabilities.state.svelte.js";
    import { PowerTable } from "@muonw/powertable";
    import { onMount, tick, onDestroy, untrack } from "svelte";
    import { user } from "../../user/user.state.svelte.js";
    import powerTableService from "../../../services/powerTable.js";
    import TagForm from "../../tag/_form.svelte";
    import RemoveVulnerabilityTag from "./remove-vulnerability-tag.svelte";
    import { notificate } from "../../../services/notification.js";
    import { isJson } from "../../../js/utils.js";
    import { locale, format } from "svelte-i18n";
    import MassiveActionsService from "../../../services/massive-actions.js";
    import VulnerabilityTitleRow from "../../../components/powerTable/vulnerability-title-row.svelte";
    import dayjs from "dayjs";
    import TableReference from "../../../components/table-reference.svelte";
    import vulnerabilitiesAPI from "../../vulnerability/vulnerabilities.api.js";

    dayjs.locale($locale);

    let {
        ptInstructs = [],
        ptOptions = [],
        tags = [],
        showActions = true,
        handleRefresh = (x) => x,
        graphicsData = {},
    } = $props();

    $effect(() => {
        if (vulnerabilities.list.data.length > 0) {
            untrack(() => {
                if (data.length > 0) hideRows();
            });
        }
    });

    const setVulsFindingCount = (data) => {
        // use map to clone object and avoid unsafe mutation
        // ?? operator returns value of the left if it is NOT null or undefined, otherwise returns value on the right
        return data.map((vul) => {
            const count = vul.count_by_status ?? {};

            return {
                ...vul,
                open: count.open ?? 0,
                accepted: count.accepted ?? 0,
                fixed: count.fixed ?? 0,
                false_positive: count.false_positive ?? 0,
            };
        });
    };

    let data = $derived.by(() => {
        return setVulsFindingCount(vulnerabilities.list.data);
    });

    let selected_rows = $state([]);
    let lang = $state($locale);
    let checkbox;
    let hidden_results_length = $state(0);
    let tableElement = $state();
    let table_is_expanded = $state(true);
    let reload_table = $state(false);
    let searchInput = "";

    tags = tags.sort((a, b) => a.name.localeCompare(b.name));

    /* START LIST FUNCTIONS */

    const showContent = (kind, id) => {
        let visibleElement = document.getElementById(kind + "-visible-" + id);
        let hiddenElement = document.getElementById(kind + "-hidden-" + id);
        visibleElement.classList.remove("is-visible");
        visibleElement.classList.add("is-hidden");
        hiddenElement.classList.remove("is-hidden");
        hiddenElement.classList.add("is-visible");
    };

    const hideContent = (kind, id) => {
        let visibleElement = document.getElementById(kind + "-visible-" + id);
        let hiddenElement = document.getElementById(kind + "-hidden-" + id);
        visibleElement.classList.add("is-visible");
        visibleElement.classList.remove("is-hidden");
        hiddenElement.classList.add("is-hidden");
        hiddenElement.classList.remove("is-visible");
    };

    const selectAll = async (e) => {
        let tData = tableElement.getData(false, ["pageData", "matchedData"]);
        let pageData = tData.pageData;
        if (!pageData || pageData.length === 0) return selected_rows;

        // 1. Check if the page is fully selected to determine select vs deselect mode
        const selectedIds = new Set(selected_rows.map((r) => r.id));
        const allPageSelected = pageData.every((el) => selectedIds.has(el.id));

        let action = allPageSelected ? "deselect" : "select";

        // 2. Handle selection logic
        if (action === "select") {
            let result;
            if (tData.matchedData.length > 10) {
                result = await notificate("select_all", "modal", {});

                if (!result.isConfirmed && result.dismiss !== "cancel") {
                    return selected_rows;
                }
            }

            let target_elements =
                result && result.isConfirmed ? tData.matchedData : pageData;
            const currentIds = new Set(selected_rows.map((x) => x.id));
            let toAdd = target_elements.filter((x) => !currentIds.has(x.id));

            selected_rows = [...selected_rows, ...toAdd];
        } else {
            // Deselect mode: clear everything
            selected_rows = [];
        }

        // 3. Update DOM and action buttons
        powerTableService.toggleActionButtons(selected_rows);

        const domRows = document.querySelectorAll(
            `div[data-prefix='vulnerabilityTable'] table tbody tr`,
        );

        // Map the PT_ID to a fast lookup set of strict strings
        const selectedPtIds = new Set(
            selected_rows.map((r) => String(r.__PT_ID__)),
        );

        domRows.forEach((row) => {
            let rowId = row.getAttribute("data-id");
            if (selectedPtIds.has(rowId)) {
                row.classList.add("selected");
            } else {
                row.classList.remove("selected");
            }
        });

        return selected_rows;
    };

    const deselectAll = () => {
        selected_rows = powerTableService.deselectAll();
        powerTableService.toggleActionButtons(selected_rows);
        return selected_rows;
    };

    const selectAllEventListener = () => {
        checkbox = document.querySelector(
            "th[data-key='select'][data-sortable='false']",
        );
        if (checkbox) {
            checkbox.addEventListener("click", selectAll);
        }
    };

    const selectRow = (event) => {
        if (!showActions) return;
        let tData = tableElement.getData(false, [
            "options",
            "instructs",
            "data",
            "search",
            "filters",
            "pageData",
            "matchedData",
        ]);
        let current = event.detail.data;
        let idx = selected_rows.findIndex((r) => r.id == current.id);
        selected_rows = powerTableService.selectRows(
            selected_rows,
            current,
            idx,
            "vulnerabilityTable",
            tData.pageData,
        );
        powerTableService.toggleActionButtons(selected_rows);
        return selected_rows;
    };

    const parseRows = (pageContent) => {
        pageContent.forEach((row) => {
            if (row["priority"]) {
                row["priority"] =
                    `<div class="cvss-badge cvss-${row.level}" style="--mwidth: 75px">${parseFloat(row.priority).toPrecision(2)}</div>`;
            }
            if (ptInstructs.findIndex((col) => col.key == "title_es") > -1) {
                row["title_es"] = {
                    title: row.title,
                    title_es: row.title_es,
                    id: row.id,
                };
            }
            if (ptInstructs.findIndex((col) => col.key == "title") > -1) {
                row["title"] = { title: row.title, id: row.id };
            }
            if (ptOptions.uniquePrefix === "customVulnerabilityTable") {
                row["del"] =
                    `<i class="fa fa-trash vlsk-red" onclick="handleDelete('${row.id}')" style="cursor: pointer;"></i>`;
            }

            if (row["tag"]) {
                row.tag = powerTableService.parseTagsRow(
                    row.tag,
                    row.__PT_ID__,
                );
            }

            if (row["epss"]) {
                row.epss =
                    row.epss < 0.01
                        ? "< 1%"
                        : `${Math.round((parseFloat(row.epss) * 100).toFixed(2))}%`;
            }

            if (!row.epss) {
                row.epss = "-";
            }

            if (!row["cvss"]) {
                row.cvss = "-";
            }

            if (parseInt(row.open) + parseInt(row.accepted) <= 0) {
                blurRow(row);
            } else {
                unblurRow(row);
            }
        });
        deselectAll();
        return pageContent;
    };

    const customSearch = (searchObj, tData) => {
        if (searchObj == "") return (data = vulnerabilities.list.data);
        let result = JSON.parse(JSON.stringify(tData));
        let specialInstructs = {};
        let words = searchObj.trim().toLowerCase().match(/\S+/g);
        result = result.filter((d) => {
            let unmatchedWords = Object.assign([], words);
            for (let key of Object.keys(d)) {
                if (!specialInstructs.hasOwnProperty(key)) {
                    let searchableString = d?.[key]?.toString()?.toLowerCase();
                    unmatchedWords = unmatchedWords.filter((word) => {
                        if (searchableString?.indexOf(word) > -1) {
                            return false;
                        }
                        return true;
                    });
                }
            }
            return !unmatchedWords.length;
        });
        return (data = result);
    };

    const hideRows = () => {
        data = data.filter(
            (item) =>
                item.count_by_status.open + item.count_by_status.accepted > 0,
        );

        if (
            ptInstructs.findIndex((col) => col.key == "fixed") > -1 &&
            ptInstructs.findIndex((col) => col.key == "false_positive") > -1
        ) {
            hideColumns();
        }

        let currentPage = tableElement.getData(true, ["options"]).options
            .currentPage;
        ptOptions.currentPage = currentPage;
        table_is_expanded = false;
    };

    const showRows = async () => {
        data = setVulsFindingCount(vulnerabilities.list.data);
        await tick();
        let currentPage = tableElement.getData(true, ["options"]).options
            .currentPage;
        if (
            ptInstructs.findIndex((col) => col.key == "fixed") == -1 &&
            ptInstructs.findIndex((col) => col.key == "false_positive") == -1
        ) {
            showColumns();
        }

        let element = document.getElementById("outOfScopeResults");
        if (element) element.remove();

        ptOptions.currentPage = currentPage;
        table_is_expanded = true;
    };

    const blurRow = async (row) => {
        await tick();
        let index = row.__PT_ID__;
        let rowToBlur = document.querySelector(`[data-id='${index}']`);
        if (rowToBlur) rowToBlur.style.opacity = "0.7";
    };

    const unblurRow = async (row) => {
        await tick();
        let index = row.__PT_ID__;
        let rowToUnblur = document.querySelector(`[data-id='${index}']`);
        if (rowToUnblur) rowToUnblur.style.opacity = "1";
    };

    const changeTitleLang = (new_lang) => {
        lang = new_lang;
        let newInstructs;
        if (new_lang == "en") {
            newInstructs = ptInstructs.filter((col) => col.key != "title_es");
            newInstructs.splice(2, 0, {
                key: "title",
                title: $format("list.columns.title"),
                filterable: false,
                parseAs: "component",
                dataComponent: VulnerabilityTitleRow,
            });
        }
        if (new_lang == "es") {
            newInstructs = ptInstructs.filter((col) => col.key != "title");
            if (ptInstructs.findIndex((col) => col.key == "title_es") > 0) {
                return;
            } else {
                newInstructs.splice(2, 0, {
                    key: "title_es",
                    title: $format("list.columns.title"),
                    filterable: false,
                    parseAs: "component",
                    dataComponent: VulnerabilityTitleRow,
                });
            }
        }
        ptInstructs.length = 0;
        ptInstructs.push(...newInstructs);
        reload_table = !reload_table;
    };

    const showColumns = () => {
        let pos = ptInstructs.findIndex((col) => col.key === "accepted");
        ptInstructs.splice(pos + 1, 0, {
            key: "fixed",
            title: $format("list.columns.fixed"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        });
        ptInstructs.splice(pos + 2, 0, {
            key: "false_positive",
            title: $format("list.columns.false_positive"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        });
        reload_table = !reload_table;
    };

    const hideColumns = () => {
        let pos_fixed = ptInstructs.findIndex((col) => col.key === "fixed");
        if (pos_fixed > -1) {
            ptInstructs.splice(pos_fixed, 1);
        }
        let pos_fp = ptInstructs.findIndex(
            (col) => col.key === "false_positive",
        );
        if (pos_fp > -1) {
            ptInstructs.splice(pos_fp, 1);
        }
        reload_table = !reload_table;
    };

    ptOptions.userFunctions = {
        customSearch: customSearch,
        customParse: parseRows,
    };

    /* END LIST FUNCTIONS */

    const openTagModal = () => {
        jQuery("#tag-add-modal").modal("show");
    };

    const closeTagModal = () => {
        jQuery("#tag-add-modal").modal("hide");
        let element = document.getElementById("tagsSelect");
        if (element) element.selectedIndex = 0;
    };

    const openTagRemoveModal = () => {
        jQuery("#tag-remove-modal").modal("show");
    };

    const closeRemoveTagModal = async () => {
        jQuery("#tag-remove-modal").modal("hide");
        document.getElementById("tagsSelect").selectedIndex = 0;
        let newData = await hydrateVulnerabilitiesWithTags(data);
        vulnerabilities.list.data = newData;
    };

    const handleAddTag = (event) => {
        let tag = { target: { value: event.detail.savedTag.id } };
        handleTagEditFn(tag);
    };

    const handleTagEditFn = async (element) => {
        let value = element.target.value;
        let requestData = [];

        if (value == "cancel") return;
        if (value == "removeTags") {
            if (selected_rows.length > 1)
                return notificate("select_only_one", "modal");
            return openTagRemoveModal();
        }
        if (value == "addNewTag") {
            if (selected_rows.length <= 0)
                return notificate("select_element", "modal");
            return openTagModal();
        } else {
            let nodes = selected_rows;
            let vulnerability_ids = new Set(nodes.map((node) => node.id));
            let vulnerabilitiesToEdit = data.filter((vulnerability) =>
                vulnerability_ids.has(vulnerability.id),
            );

            if (vulnerabilitiesToEdit.length === 0) {
                return notificate("select_element", "modal");
            }
            for (let vulnerability of vulnerabilitiesToEdit) {
                requestData.push({
                    tag_id: value,
                    target_object_type: "Vulnerability",
                    target_object_id: vulnerability.id,
                });
            }
            let response = await MassiveActionsService.handleTagAssociation(
                requestData,
                {},
            );
            if (response.errors.length === 0) {
                notificate("save_ok", "toast", {
                    data: $format("vulnerability.plural"),
                    is_massive: true,
                });
                let newData = await hydrateVulnerabilitiesWithTags(data);
                replaceVulnerabilitiesListElements(newData);
            } else {
                let failed_vulnerabilities_ids = [];
                for (let error of response.errors) {
                    failed_vulnerabilities_ids.push(error.target_object_id);
                }
                let failed = vulnerabilitiesToEdit.filter((vulnerability) =>
                    failed_vulnerabilities_ids.includes(vulnerability.id),
                );
                let newData = await hydrateVulnerabilitiesWithTags(data);
                vulnerabilities.list.data = newData;
                for (let fail of failed) {
                    let toastConfig = {
                        data: fail.name,
                        options: {
                            timeOut: "0",
                            extendedTimeOut: "0",
                            showDuration: "0",
                            hideDuration: "0",
                            closeButton: true,
                        },
                    };
                    notificate("save_error", "toast", toastConfig);
                }
            }
        }
        closeTagModal();
        selected_rows = [];
    };

    const handleKeydown = (event) => {
        let page = tableElement.getData().options.currentPage;
        if (event.keyCode == 39) {
            ptOptions.currentPage = page + 1;
        }
        if (event.keyCode == 37) {
            if (page - 1 > 0) {
                ptOptions.currentPage = page - 1;
            } else {
                return;
            }
        } else {
            return;
        }
    };

    /* START EXPORT FUNCTIONS */

    const exportJsonData = (e) => {
        let currentData = tableElement.getData(
            false,
            "matchedData",
        ).matchedData;
        for (let item of currentData) {
            delete item.__PT_ID__;
            delete item.__PT_CB__;
            for (let [k, v] of Object.entries(item)) {
                if (typeof v === "string" && isJson(item[k])) {
                    item[k] = JSON.parse(v);
                }
            }
        }
        // Download JSON object as a file from browser (https://stackoverflow.com/a/30800715)
        let dataStr =
            "data:text/json;charset=utf-8," +
            encodeURIComponent(JSON.stringify(currentData));
        let downloadAnchorNode = document.createElement("a");
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute(
            "download",
            "Vulnerabilidades_vulseek.json",
        );
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    };

    const exportToCSV = async () => {
        let instructs = ptInstructs;
        let header = [];
        let titles = [];
        const items = tableElement.getData(false, "matchedData").matchedData;
        for (let item of items) {
            if (item.priority) {
                item.priority = parseFloat(item.priority).toPrecision(2);
            }
            let tags = isJson(item.tag) ? JSON.parse(item.tag) : [];
            let tagNames = [];
            for (let tag of tags) {
                tagNames.push(tag.name);
            }
            item.tag = tagNames.join(", ");
        }
        const replacer = (key, value) => (value === null ? "" : value); // specify how you want to handle null values here
        Object.keys(items[0]).forEach((item, i) => {
            instructs.forEach((col) => {
                if (col.key === item) {
                    if (
                        col.title == "Falso Positivo" ||
                        col.title === "Falso positivo"
                    )
                        col.title = col.title.replace(" ", "_");
                    header.push({ key: item, value: col.title });
                    titles.push(col.title);
                }
            });
        });
        const csv = [
            titles.join(";"), // header row first
            ...items.map((row) =>
                header
                    .map((fieldName) =>
                        JSON.stringify(row[fieldName["key"]], replacer),
                    )
                    .join(";"),
            ),
        ].join("\r\n");

        let hiddenElement = document.createElement("a");
        hiddenElement.href =
            "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
        hiddenElement.target = "_blank";
        hiddenElement.download = "vulnerabilidades_vulseek.csv";
        hiddenElement.click();
    };

    /* END EXPORT FUNCTIONS */

    /* START COPY ALL DATA FUNCTION */

    const copyAllDataToClipboard = async () => {
        try {
            // Determine current language
            const currentLang = $locale;

            // Generate CSV table data (reuse exportToCSV logic)
            let instructs = ptInstructs;
            let header = [];
            let titles = [];
            const items = tableElement.getData(
                false,
                "matchedData",
            ).matchedData;
            for (let item of items) {
                if (item.priority) {
                    item.priority = parseFloat(item.priority).toPrecision(2);
                }
                let tags = isJson(item.tag) ? JSON.parse(item.tag) : [];
                let tagNames = [];
                for (let tag of tags) {
                    tagNames.push(tag.name);
                }
                item.tag = tagNames.join(", ");
            }
            const replacer = (key, value) => (value === null ? "" : value); // specify how you want to handle null values here
            Object.keys(items[0]).forEach((item, i) => {
                instructs.forEach((col) => {
                    if (col.key === item) {
                        if (
                            col.title == "Falso Positivo" ||
                            col.title === "Falso positivo"
                        )
                            col.title = col.title.replace(" ", "_");
                        header.push({ key: item, value: col.title });
                        titles.push(col.title);
                    }
                });
            });
            const csv = [
                titles.join(";"), // header row first
                ...items.map((row) =>
                    header
                        .map((fieldName) =>
                            JSON.stringify(row[fieldName["key"]], replacer),
                        )
                        .join(";"),
                ),
            ].join("\r\n");

            // Transform vulnerability kinds data to use proper names
            const vulKinds = vulnerabilitiesAPI.kinds();
            const vulKindMap = {};
            vulKinds.forEach((kind) => {
                const prefix = kind.id.replace("-", "");
                vulKindMap[prefix] = kind.name;
            });

            // Transform the vulnerability kinds data to use proper names
            const transformedVulKinds = {};
            if (graphicsData.vulsByKind) {
                Object.entries(graphicsData.vulsByKind).forEach(
                    ([key, value]) => {
                        const displayName = vulKindMap[key] || key;
                        transformedVulKinds[displayName] = value;
                    },
                );
            }
            let topFiveVuls = [];
            for (let vul of graphicsData.topFiveVuls) {
                topFiveVuls.push({
                    id: vul.id,
                    title: vul.title,
                    title_es: vul.title_es,
                    priority: vul.priority,
                    cvss: vul.cvss,
                    priority_level: vul.priority_level,
                    findings_by_status: {
                        open: vul.count_by_status.open,
                        accepted: vul.count_by_status.accepted,
                        fixed: vul.count_by_status.fixed,
                        false_positive: vul.count_by_status.false_positive,
                    },
                });
            }
            // Generate JSON graphics data with proper titles
            const graphicsSection = {
                [currentLang === "es"
                    ? "vulnerabilidades_por_tipo"
                    : "vulnerabilities_by_kind"]: transformedVulKinds,
                [currentLang === "es"
                    ? "top_5_vulnerabilidades_con_mas_hallazgos"
                    : "top_5_vulnerabilities_with_most_findings"]:
                    topFiveVuls || [],
            };

            // Combine both datasets with clear, language-specific headers
            const combinedData =
                `# ${currentLang === "es" ? "VULNERABILIDADES" : "VULNERABILITIES"} (CSV)\n` +
                csv +
                `\n\n# ${currentLang === "es" ? "GRÁFICOS" : "GRAPHICS"} (JSON)\n` +
                JSON.stringify(graphicsSection, null, 2);

            // Copy to clipboard
            await navigator.clipboard.writeText(combinedData);

            notificate("copied", "toast", {
                data: $format("copied"),
            });
        } catch (err) {
            // Show error notification
            notificate("save_error", "toast", {
                data: $format("buttons.copy_to_clipboard_failed"),
            });
        }
    };

    /* END COPY ALL DATA FUNCTION */

    onMount(async () => {
        if (ptInstructs.length <= 0) {
            ptInstructs = [
                {
                    key: "priority",
                    title: "Prioridad",
                    filterable: false,
                    parseAs: "unsafe-html",
                    userFunctions: {
                        customSort: powerTableService.sortNumbers,
                    },
                },
                {
                    key: "title_es",
                    title: "aaa",
                    filterable: false,
                    parseAs: "component",
                    dataComponent: VulnerabilityTitleRow,
                },
                {
                    key: "open",
                    title: "Abierto",
                    filterable: false,
                    userFunctions: {
                        customSort: powerTableService.sortNumbers,
                    },
                },
                {
                    key: "accepted",
                    title: "Aceptado",
                    filterable: false,
                    userFunctions: {
                        customSort: powerTableService.sortNumbers,
                    },
                },
                {
                    key: "fixed",
                    title: "Solucionado",
                    filterable: false,
                    userFunctions: {
                        customSort: powerTableService.sortNumbers,
                    },
                },
                {
                    key: "false_positive",
                    title: "Falso Positivo",
                    filterable: false,
                    userFunctions: {
                        customSort: powerTableService.sortNumbers,
                    },
                },
                {
                    key: "cvss",
                    title: "CVSS",
                    filterable: false,
                    userFunctions: {
                        customSort: powerTableService.sortNumbers,
                    },
                },
                {
                    key: "epss",
                    title: "EPSS",
                    filterable: false,
                    userFunctions: {
                        customSort: powerTableService.sortNumbers,
                    },
                },
            ];
        }
        await tick();

        selectAllEventListener();
    });

    onDestroy(() => {
        if (checkbox) checkbox.removeEventListener("click", selectAll);
    });

    window.showContent = showContent;
    window.hideContent = hideContent;
</script>

<svelte:window onkeydown={handleKeydown} />
<div class="card mt-3">
    <div class="card-header">
        {#if !showActions}
            <div class="card-title">
                {$format("list.columns.vulnerabilities")}
            </div>
        {/if}
        <div class="float-start">
            {#if ptInstructs.length > 0}
                <TableReference
                    columnsDefinition={ptInstructs}
                    list={"vulnerability"}
                />
            {/if}
        </div>
        <div class="float-end">
            {#if $locale != "en" && showActions}
                <button
                    class="btn btn-secondary table-button btn-language btn-xs"
                    title={lang == "es"
                        ? "Ver idioma original"
                        : "Ver idioma seleccionado"}
                    onclick={() => changeTitleLang(lang == "es" ? "en" : "es")}
                >
                    {#key lang}
                        {#if lang == "es"}
                            <i class="flag-icon flag-icon-ar"></i>
                        {:else}
                            <i class="flag-icon flag-icon-us"></i>
                        {/if}
                    {/key}
                </button>
            {/if}

            {#if table_is_expanded}
                <button
                    class="btn btn-secondary table-button btn-compress btn-xs t-compress-vulnerability-btn"
                    tabindex="0"
                    aria-controls={ptOptions.uniquePrefix}
                    type="button"
                    title={$format("buttons.hide_fixed_and_fp")}
                    id="vulnerabilityCompressBtn"
                    onclick={hideRows}
                    ><i class="fas fa-compress-arrows-alt"></i></button
                >
            {:else}
                <button
                    class="btn btn-secondary table-button btn-expand btn-xs t-expand-vulnerability-btn"
                    tabindex="0"
                    aria-controls={ptOptions.uniquePrefix}
                    type="button"
                    title={$format("buttons.show_fixed_and_fp")}
                    id="vulnerabilityExpandBtn"
                    onclick={showRows}
                    ><i class="fas fa-expand-arrows-alt"></i></button
                >
            {/if}
            {#if showActions}
                <button
                    class="btn btn-secondary table-button btn-refresh btn-xs t-refresh-vulnerability-btn"
                    tabindex="0"
                    aria-controls={ptOptions.uniquePrefix}
                    type="button"
                    title={$format("buttons.refresh")}
                    id="vulnerabilityRefreshBtn"
                    onclick={handleRefresh}
                    ><i class="fa fa-sync-alt"></i></button
                >
            {/if}
            <button
                class="btn btn-secondary table-button btn-export btn-xs t-json-vulnerability-btn"
                tabindex="0"
                aria-controls={ptOptions.uniquePrefix}
                type="button"
                title={$format("buttons.json")}
                id="tagJSONbtn"
                onclick={exportJsonData}
                ><i class="fab fa-js-square" style="font-size: 18px;"
                ></i></button
            >
            <button
                class="btn btn-secondary table-button btn-export btn-xs t-csv-vulnerability-btn"
                tabindex="0"
                aria-controls={ptOptions.uniquePrefix}
                type="button"
                title={$format("buttons.csv")}
                id="vulnerabilityCSVbtn"
                onclick={exportToCSV}
                ><i class="fa fa-file-csv" style="font-size: 18px;"></i></button
            >
            <button
                class="btn btn-secondary table-button btn-export btn-xs t-copy-all-btn"
                tabindex="0"
                aria-controls={ptOptions.uniquePrefix}
                type="button"
                title={$format("buttons.copy_to_clipboard")}
                id="vulnerabilityCopyAllBtn"
                onclick={copyAllDataToClipboard}
                ><i class="fa fa-copy" style="font-size: 18px;"></i></button
            >
            {#if showActions && user.data.role != "viewer"}
                <select
                    disabled
                    id="tagsSelect"
                    name="tagsSelect"
                    class="form-control btn-disabled"
                    style="height: 28px; clear: left; float: right; max-width: 125px;font-size: 12px; margin-right: 10px;"
                    oninput={(e) => handleTagEditFn(e)}
                >
                    <option value="cancel"
                        >{$format("buttons.select_tag.select")}</option
                    >
                    <option disabled>----------</option>
                    <option value="addNewTag"
                        >{$format(
                            "buttons.select_tag.add_and_associate",
                        )}</option
                    >
                    <option disabled>----------</option>
                    <option value="removeTags"
                        >{$format("buttons.select_tag.remove")}</option
                    >
                    <option disabled>----------</option>
                    {#each tags as tag}
                        <option value={tag.id}>{tag.name}</option>
                    {/each}
                </select>
            {/if}
        </div>
    </div>
    <div class="card-body">
        <div class="table-responsive">
            <form class="form-inline" novalidate>
                <div class="input-group input-group-sm">
                    <input
                        class="form-control custom-search"
                        placeholder={$format("filter")}
                        data-name="search-input"
                        type="text"
                        oninput={(e) => customSearch(e.target.value, data)}
                    />
                    <div class="input-group-append">
                        <i class="fas fa-search"></i>
                    </div>
                </div>
            </form>
            {#key reload_table}
                <PowerTable
                    ptData={data}
                    {ptInstructs}
                    {ptOptions}
                    on:rowClicked={(e) => selectRow(e)}
                    bind:this={tableElement}
                >
                    <!-- {#snippet noResults()}
                        <div>
                        {$format("results_not_found")}
                        {#if out_of_scope_length > 0}
                            {@html $format("list.interface.hidden_results_text")}
                        {/if}
                    </div>
                    {/snippet} -->
                </PowerTable>
            {/key}
        </div>
    </div>
</div>

<!-- TAG MODAL -->
<div
    class="modal fade show"
    id="tag-add-modal"
    style="display: none; padding-right: 9px;"
    aria-modal="true"
    role="dialog"
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("page.tag.add")}</h4>
                <button
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                >
                    <span aria-hidden="true">×</span>
                </button>
            </div>
            <div class="modal-body">
                <TagForm
                    on:save={handleAddTag}
                    on:closeModal={closeTagModal}
                    tag={null}
                />
            </div>
        </div>
        <!-- /.modal-content -->
    </div>
    <!-- /.modal-dialog -->
</div>
<!-- END TAG MODAL-->

<!-- REMOVE TAG MODAL -->
<div
    class="modal fade show"
    id="tag-remove-modal"
    style="display: none; padding-right: 9px;"
    aria-modal="true"
    role="dialog"
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("page.tag.remove")}</h4>
                <button
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                >
                    <span aria-hidden="true">×</span>
                </button>
            </div>
            <div class="modal-body">
                {#key selected_rows}
                    <RemoveVulnerabilityTag
                        vulnerability={selected_rows[0]}
                        on:closeModal={closeRemoveTagModal}
                        on:removeTags={closeRemoveTagModal}
                    />
                {/key}
            </div>
        </div>
        <!-- /.modal-content -->
    </div>
    <!-- /.modal-dialog -->
</div>

<!-- END TAG MODAL-->

<style>
    .custom-search {
        background-color: whitesmoke;
        border-color: #e8e8e8;
        color: rgba(52, 58, 64, 0.8);
    }
    .input-group-append {
        position: absolute;
        right: 25px;
        top: 0.5em;
        /*z-index: 99999;*/
    }
    .input-group-append .fas.fa-search {
        color: rgba(52, 58, 64, 0.8);
    }
    .input-group {
        width: 25%;
        position: relative;
    }
</style>
